import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  TOOL_BAR_HEIGHT,
} from '../../constants';
import SiblingsContainer from '../siblings/siblingsContainer';
import getPdfViewerApplication from '../../utils/pdfjs/pdfViewerApplication';
import XocsType from '../xocs/xocsType';

export default class Viewer extends Component {
  constructor() {
    super();
    this.pdfViewerApp = getPdfViewerApplication();
    this.viewerContainer = React.createRef();
    this.viewer = React.createRef();
  }

  componentDidMount() {
    const {
      onUpdateFindControlState,
      dispatchEnhancementEvent,
      dispatchPrintEvent,
      setActivePageIndex,
      pdfDocument,
      setInitialScale,
      setDefaultPageSize,
      articleAuthors,
      referencesContext,
      references,
      figures,
    } = this.props;

    const onPageChange = evt => setActivePageIndex(evt.pageNumber - 1);

    this.pdfViewerApp.init(
      this.viewerContainer.current,
      this.viewer.current,
      onUpdateFindControlState,
      onPageChange,
      dispatchEnhancementEvent,
      dispatchPrintEvent,
    );

    this.pdfViewerApp.updateEnhancementsContext({
      articleAuthors,
      referencesContext,
      references,
      figures,
    });

    return pdfDocument.getPage(1).then((firstPage) => {
      const viewport = firstPage.getViewport({ scale: 1 });
      const pageSize = {
        width: viewport.width,
        height: viewport.height,
      };
      // Scale should be set before setDocument() to avoid flash
      setInitialScale(pageSize.width);
      setDefaultPageSize(pageSize);
      this.pdfViewerApp.setDocument(pdfDocument);

      window.removeErrorHandler();
    });
  }

  componentDidUpdate() {
    const { rotations } = this.props;
    this.setPageRotations(rotations);
    this.pdfViewerApp.updateEnhancementsContext(this.props);
  }

  static onCopy(event) {
    event.clipboardData.setData('text/plain', document.getSelection()?.toString());
    event.preventDefault();
  }

  setPageRotations(rotations) {
    rotations.forEach(({ index, degree }) => {
      this.pdfViewerApp.setPageRotation(index, degree);
    });
  }

  render() {
    const {
      sidePanelOpen, obscureViewer, clientHeight, clientWidth, language, sidePanelWidth,
    } = this.props;
    const width = sidePanelOpen ? clientWidth - sidePanelWidth : clientWidth;
    const height = clientHeight - TOOL_BAR_HEIGHT;
    return (
      <div className="viewer-container">
        <div
          className={classnames('pdfjs-viewer', {
            'pdfjs-viewer-obscured': obscureViewer,
            'intercom-enabled': window?.config?.feature?.intercom && window?.pageData?.visitor?.accountId,
          })}
          id="viewer"
          style={{ width, height }}
          ref={this.viewerContainer}
          onCopy={Viewer.onCopy}
        >
          <SiblingsContainer stackOrder="top" />
          <h1 className="u-hide-visually">Article start</h1>
          <div id="pdfViewer" className="pdfViewer" ref={this.viewer} lang={language} />
          <SiblingsContainer stackOrder="bottom" />
        </div>
      </div>
    );
  }
}

Viewer.propTypes = {
  pdfDocument: PropTypes.shape({
    getPage: PropTypes.func,
  }),
  rotations: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number,
    degree: PropTypes.number,
  })),
  sidePanelOpen: PropTypes.bool,
  obscureViewer: PropTypes.bool,
  language: PropTypes.string,
  clientHeight: PropTypes.number.isRequired,
  clientWidth: PropTypes.number.isRequired,
  sidePanelWidth: PropTypes.number.isRequired,
  setActivePageIndex: PropTypes.func.isRequired,
  setInitialScale: PropTypes.func.isRequired,
  setDefaultPageSize: PropTypes.func.isRequired,
  onUpdateFindControlState: PropTypes.func.isRequired,
  dispatchEnhancementEvent: PropTypes.func.isRequired,
  dispatchPrintEvent: PropTypes.func.isRequired,
  articleAuthors: PropTypes.arrayOf(PropTypes.shape({
    authorId: PropTypes.string,
    givenName: PropTypes.string,
    surname: PropTypes.string,
  })),
  referencesContext: PropTypes.arrayOf(PropTypes.shape({
    refId: PropTypes.string,
    preceding: PropTypes.string,
    text: PropTypes.string,
    following: PropTypes.string,
    type: PropTypes.string,
  })),
  references: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        referenceList: PropTypes.arrayOf(XocsType),
      }),
    ),
    isLoading: PropTypes.bool,
    rangeIndex: PropTypes.number,
  }),
  figures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      caption: XocsType,
      figure: PropTypes.shape({
        altText: PropTypes.string,
        large: PropTypes.string,
        regular: PropTypes.string,
        small: PropTypes.string,
      }),
    }),
  ),
};

Viewer.defaultProps = {
  pdfDocument: null,
  sidePanelOpen: false,
  obscureViewer: false,
  rotations: [],
  articleAuthors: null,
  referencesContext: null,
  references: {
    data: [],
    isLoading: false,
    rangeIndex: 0,
  },
  figures: null,
  language: undefined,
};
